import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { ClipboardList, X } from "lucide-react"
import { normalizeStr } from "@/app/lib/str"
import { secondaryButton, primaryButton } from "@/app/lib/buttonStyles"
import { controlBase, onFocus, onBlur } from "@/app/lib/tableStyles"
import { usePopover } from "@/app/hooks/usePopover"
import type { ClientTheme } from "@/app/lib/themes"

interface StrPastePanelProps {
  /** Canonical STRs present in the currently-loaded schedule, for match counting. */
  strOptions: string[]
  /** Replaces the page's STR filter with the matched STRs and closes the panel. */
  onApply: (strs: string[]) => void
  theme: ClientTheme
}

// Toggleable paste-a-list control: parse a pasted block of STRs (any
// separator), normalize with the shared STR twin, and report parsed/matched/
// unmatched counts before applying as the STR filter. Ported from the
// prototype's `strPaste` memo (assignment-extraction-agent_9.10.26.jsx
// ~line 1847).
export function StrPastePanel({ strOptions, onApply, theme }: StrPastePanelProps) {
  const { open, setOpen, triggerRef, popoverRef, popoverStyle } = usePopover<HTMLDivElement>()
  const [text, setText] = useState("")
  const panelWidth = Math.min(360, window.innerWidth - 16)

  // Outside-click and Escape close the popover via usePopover; start fresh
  // the next time it opens.
  useEffect(() => {
    if (!open) setText("")
  }, [open])

  const optionSet = useMemo(() => new Set(strOptions), [strOptions])

  const result = useMemo(() => {
    const tokens = text
      .split(/[\s,;]+/)
      .map((t) => t.trim())
      .filter(Boolean)
    const seen = new Set<string>()
    const normalized: string[] = []
    for (const t of tokens) {
      const n = normalizeStr(t)
      if (!n || seen.has(n)) continue
      seen.add(n)
      normalized.push(n)
    }
    const matched = normalized.filter((s) => optionSet.has(s))
    const unmatched = normalized.filter((s) => !optionSet.has(s))
    return { parsedCount: normalized.length, matched, unmatched }
  }, [text, optionSet])

  const close = () => setOpen(false)

  return (
    <div ref={triggerRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={onFocus(theme)}
        onBlur={onBlur(theme)}
        className="flex items-center gap-1.5"
        style={{
          ...controlBase(theme),
          paddingLeft: "10px",
          paddingRight: "10px",
          cursor: "pointer",
          color: theme.textMuted,
        }}
      >
        <ClipboardList size={13} /> Paste STR list
      </button>

      {/* Portaled to <body> so the card's overflow-hidden can't clip it. */}
      {open &&
        popoverStyle &&
        createPortal(
          <div
            ref={popoverRef}
            className="rounded-lg p-3"
            style={{
              ...popoverStyle,
              // Anchored to the trigger's left edge, but kept inside the viewport.
              left: Math.max(8, Math.min(popoverStyle.left, window.innerWidth - panelWidth - 8)),
              overflowY: "auto",
              zIndex: 60,
              width: `${panelWidth}px`,
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: theme.textPrimary,
                }}
              >
                Paste a list of STRs
              </span>
              <button
                type="button"
                onClick={close}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: theme.textDim,
                }}
              >
                <X size={14} />
              </button>
            </div>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"14-13N-19W\n15-13N-19W, 3-12N-18W …"}
              rows={5}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: "8px",
                backgroundColor: theme.bgSurface2,
                border: `1px solid ${theme.borderColor}`,
                color: theme.textPrimary,
                fontFamily: "var(--font-data)",
                fontSize: "12px",
                outline: "none",
                resize: "vertical",
              }}
            />
            {text.trim() && (
              <div
                className="mt-2"
                style={{ fontFamily: "var(--font-ui)", fontSize: "11.5px", color: theme.textMuted }}
              >
                {result.parsedCount} parsed · {result.matched.length} matched
                {result.unmatched.length > 0 ? ` · ${result.unmatched.length} unmatched` : ""}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={close}
                className="btn"
                style={{ ...secondaryButton(theme), padding: "6px 12px" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onApply(result.matched)
                  close()
                }}
                disabled={result.matched.length === 0}
                className="btn"
                style={{
                  ...primaryButton(theme),
                  padding: "6px 12px",
                  opacity: result.matched.length === 0 ? 0.6 : 1,
                  cursor: result.matched.length === 0 ? "default" : "pointer",
                }}
              >
                Apply filter
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
